import React from 'react';
import { Link } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Guideline, SliderCheckbox, Tooltip } from '@components/common';
import Actions from '@components/common/Actions';

import { isEmpty } from '@utils/common';
import { checkIsPlatformOwner } from '@utils/users';

import { ITitle } from '@interfaces/common/common';
import { ChangeStatusUserDTO } from '@interfaces/organisation/dto/organisationsDTO';
import { IUserOrganisation } from '@interfaces/user/dto/userProfileDTO';
import { IUserEntity } from '@interfaces/user/user';

import { EMultiOrgUserStatuses } from '@enums/common';

import colors from '@constants/colors';
import { routes } from '@constants/routes';
import { columnSizes } from '@constants/table';
import {
	approvalUnitDeleteRestrictions,
	approvalUnitStatusRestrictions,
	combinedDeleteRestriction,
	combinedStatusRestriction,
	editTooltipText,
	lastAdminDeleteRestriction,
	lastAdminStatusRestriction,
	mixedUserTooltip,
	noAssignedOrganisationsStatusRestrictions,
	platformOwnerRestrictions,
	removeTooltipText,
	viewTooltipText,
} from '@components/UsersTable/constants';

import styles from './UsersTable.module.scss';
import tableStyles from '@components/common/Table/Table.module.scss';

const { adminPanel: { index, organisations } } = routes;

interface IGetColumns {
	openDeleteConfirm: () => void;
	handleChangeStatusUser: (data: ChangeStatusUserDTO) => void;
	openUserModal: () => void;
	setEditableUser: React.Dispatch<React.SetStateAction<IUserEntity | undefined>>;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
	viewAsOwner: boolean;
	viewAsOrganizationAdministrator: boolean;
	isOrganisationProfile: boolean;
	isManageUsersTable?: boolean;
	isEditAccess: boolean;
	isRemoveAccess: boolean;
}

export const getColumns = ({
	openDeleteConfirm,
	setSingleIdForRemove,
	openUserModal,
	setEditableUser,
	handleChangeStatusUser,
	setIsEditMode,
	setIsViewMode,
	viewAsOwner,
	viewAsOrganizationAdministrator,
	isOrganisationProfile,
	isManageUsersTable,
	isEditAccess,
	isRemoveAccess,
}: IGetColumns) => ([
	{
		header: 'First Name',
		accessorKey: 'firstName',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value}/>
			);
		},
	},
	{
		header: 'Last Name',
		accessorKey: 'lastName',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value}/>
			);
		},
	},
	{
		header: 'E-mail',
		accessorKey: 'email',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value}/>
			);
		},
	},
	{
		header: 'Status',
		accessorKey: 'isEnabled',
		size: columnSizes.size105,
		cell: ({ row }: CellContext<any, any>) => {
			const isLastAdminInAnyOrganization = row?.original?.isLastAdminInAnyOrganization
				|| row?.original?.isLastAdmin;
			const status = row?.original?.status?.name;
			const isInApprovalUnit = !!row?.original?.isAssignedToApprovalUnits
				|| !!row?.original?.isAssignedToApprovalUnitsInAnyOrganization;
			const roles = row?.original?.roles?.map((role: { name: string }) => role?.name);
			const isPlatformOwner = checkIsPlatformOwner(roles);
			const isNoAssignedOrganisations = isEmpty(row?.original?.organizations);

			const checked = isManageUsersTable
				? status === EMultiOrgUserStatuses.allOn || status === EMultiOrgUserStatuses.allOnDisabled
				: row?.original?.isEnabled;

			const isDisabledBlockAction = (isLastAdminInAnyOrganization && checked) || (isInApprovalUnit && checked);

			const changeStatusDisabledManageUsersTable = (status === EMultiOrgUserStatuses.allOnDisabled
				|| status === EMultiOrgUserStatuses.allOffDisabled
				|| isDisabledBlockAction
				|| isNoAssignedOrganisations
			);

			const changeStatusDisabled = !isEditAccess || isDisabledBlockAction || isPlatformOwner;

			const getChangeStatusTooltip = () => {
				if (isPlatformOwner) {
					return platformOwnerRestrictions;
				}

				if (isInApprovalUnit && isLastAdminInAnyOrganization) {
					return combinedStatusRestriction;
				}

				if (isLastAdminInAnyOrganization) {
					return lastAdminStatusRestriction;
				}

				if (isInApprovalUnit) {
					return approvalUnitStatusRestrictions;
				}

				if (isManageUsersTable && isNoAssignedOrganisations) {
					return noAssignedOrganisationsStatusRestrictions;
				}

				return undefined;
			};

			const changeStatus = () => {
				handleChangeStatusUser({
					id: row?.original?.id,
					isActive: checked,
				});
			};

			const tooltipContent = getChangeStatusTooltip();

			const isDisabledTooltip = !isDisabledBlockAction && !isPlatformOwner && !isNoAssignedOrganisations
				|| !tooltipContent;

			return (
				<Tooltip
					content={tooltipContent}
					isDisabled={isDisabledTooltip}
					maxWidth={325}
				>
					<div className={tableStyles.checkboxWrapper}>
						{isManageUsersTable ? (
							status === EMultiOrgUserStatuses.mixed ? (
								<div className={styles.mixedUserGuideline}>
									<span>Mixed</span>
									<Guideline guidelineText={mixedUserTooltip}/>
								</div>
							) : (
								<SliderCheckbox
									checked={checked}
									disabled={changeStatusDisabledManageUsersTable}
									id={row?.original?.id}
									onChange={changeStatus}
								/>
							)
						) : (
							<SliderCheckbox
								checked={checked}
								disabled={changeStatusDisabled}
								id={row?.original?.id}
								onChange={changeStatus}
							/>
						)}
					</div>
				</Tooltip>
			);
		},
	},
	{
		header: 'Organisation name',
		accessorKey: 'organizationName',
		isHidden: !viewAsOwner || isOrganisationProfile,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			return (
				<div className={styles.organizationNameWrapper}>
					{original?.organizations?.length
						? original?.organizations?.map(({ name, id }: IUserOrganisation) => (
							<Link
								key={id}
								to={`${index}${organisations}/organisation-profile/${id}`}
							>
								<AdaptiveTableCell
									styleInline={{
										color: colors.indigo50,
										textDecoration: 'underline',
										cursor: 'pointer',
										padding: '0px',
									}}
									text={name}
								/>
							</Link>
						)) : '-'
					}
				</div>
			);
		},
	},
	{
		header: 'Role',
		accessorKey: 'roles',
		enableSorting: false,
		isHidden: !viewAsOrganizationAdministrator && !isOrganisationProfile,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value: Array<ITitle> = getValue();
			const roles = value?.map(role => role?.title)?.join(', ');

			return (
				<AdaptiveTableCell text={roles} />
			);
		},
	},
	{
		header: 'created',
		accessorKey: 'createdAt',
		size: columnSizes.size190,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const { createdAtText } = original;

			return (
				<AdaptiveTableCell text={createdAtText} />
			);
		},
	},
	{
		header: 'Last seen',
		accessorKey: 'visitedAt',
		size: columnSizes.size190,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const { visitedAtText } = original;

			return (
				<AdaptiveTableCell text={visitedAtText} />
			);
		},
	},
	{
		header: () => (
			<div className={tableStyles.centerColumnHeader}>
				Actions
			</div>
		),
		accessorKey: 'id',
		enableSorting: false,
		size: columnSizes.size95,
		isHidden: !isEditAccess,
		cell: ({ row }: CellContext<any, any>) => {
			const roles = row?.original?.roles?.map((role: { name: string }) => role?.name);
			const isPlatformOwner = checkIsPlatformOwner(roles);
			const isInApprovalUnit = !!row?.original?.isAssignedToApprovalUnits
				|| !!row?.original?.isAssignedToApprovalUnitsInAnyOrganization;
			const isLastAdminInAnyOrganization = row?.original?.isLastAdminInAnyOrganization
				|| row?.original?.isLastAdmin;

			const handleDelete = () => {
				if (isPlatformOwner) return;

				setSingleIdForRemove(row?.original?.id);
				openDeleteConfirm();
			};

			const handleView = () => {
				setIsViewMode(true);
				setEditableUser(row?.original);
				openUserModal();
			};

			const handleEdit = () => {
				if (isPlatformOwner) return;

				setIsEditMode(true);
				setEditableUser(row?.original);
				openUserModal();
			};

			const deleteActionTooltip = isPlatformOwner
				? platformOwnerRestrictions
				: isInApprovalUnit && isLastAdminInAnyOrganization
					? combinedDeleteRestriction
					: isLastAdminInAnyOrganization
						? lastAdminDeleteRestriction
						: isInApprovalUnit
							? approvalUnitDeleteRestrictions
							: removeTooltipText;

			return (
				<Actions
					deleteTooltip={deleteActionTooltip}
					editHandler={handleEdit}
					editTooltip={isPlatformOwner ? platformOwnerRestrictions : editTooltipText}
					gridNumberOfColumns={isRemoveAccess ? 2 : 1}
					handleDelete={handleDelete}
					isDelete={isRemoveAccess}
					isDeleteDisable={isLastAdminInAnyOrganization || isPlatformOwner || isInApprovalUnit}
					isEdit
					isEditDisable={isPlatformOwner}
					isView
					viewHandler={handleView}
					viewTooltip={viewTooltipText}
				/>
			);
		},
	},
]);
